import MButton from "components/momentum/MButton";
import { Controller, useForm } from "react-hook-form";
import { Dialog, DialogContent, IconButton } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { Close } from "@mui/icons-material";
import { FC, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Account } from "types/portfolio";
import { GridApi } from "ag-grid-community";
import { useAxios } from "myAxios";
import Reason from "./Reason";
import { useRouter } from "next/router";

const useStyles = makeStyles(() => ({
  titleContainer: {
    display: "flex",
    justifyContent: "space-between",
    padding: "16px 24px",
  },
  title: {
    fontSize: 20,
    fontFamily: "CiscoSansTT",
    color: "#394454",
    fontWeight: 350,
    display: "flex",
    alignItems: "center",
    letterSpacing: "0.16px",
  },
  contentWrapper: {
    display: "flex",
    alignItems: "start",
    padding: "16px 20px",
  },
  mainWrapper: {
    display: "flex",
    flexDirection: "column",
    marginLeft: 16,
  },
  text: {
    flexWrap: "wrap",
  },
  container: {
    display: "flex",
    flexWrap: "wrap",
  },
  radioGroupRoot: {
    display: "flex",
    flexDirection: "row",
    marginLeft: 3,
  },
}));

const ExceptionDialog: FC<{
  api: GridApi | undefined;
  node: any;
  valueCheck: boolean;
  open: boolean;
  onClose: () => void;
  data: Account;
  type?: string;
  // value: string;
  selected: string;
  colId: string;
  updateCallback?: (wemVal: string, aemVal?: string) => void;
  showExpMark?: boolean;
  mutate?: any;
}> = ({
  api,
  node,
  valueCheck,
  open,
  onClose,
  data,
  type = "account",
  // value,
  selected,
  colId,
  updateCallback,
  // showExpMark = false,
  mutate,
}) => {
  const classes = useStyles();
  const [updating, setUpdating] = useState(false);
  const axios = useAxios();
  const router = useRouter();
  const { handleSubmit, control, reset } = useForm();

  const getLatestWEM = () => {
    axios
      .get(`/api/dataservice/v1/accounts/${data?.accountId}?includeSites=true&usingBundle=true`)
      .then((res) => {
        const result = res.data;
        if (result) {
          node?.setDataValue(`${colId}ExpBy`, result[`${colId}ExpBy` as keyof typeof result]);
          node?.setDataValue(`${colId}ExpDate`, result[`${colId}ExpDate` as keyof typeof result]);
        }
      })
      .catch(() => {
        toast.error("Fail to get latest wem", {
          position: "top-center",
          autoClose: 3000,
        });
      });
  };

  const onSubmit = async (submitData: { [key: string]: any }) => {
    const { reason } = submitData;

    let postData: any = {
      reason: reason?.trim(),
      accountKeys: [data?.accountKey],
    };

    setUpdating(true);
    if (type === "account") {
      // AEM
      postData = {
        ...postData,
        coverageStatus: selected,
      };
    } else {
      postData = {
        // WEM
        ...postData,
        engagementValue: selected,
        wemField: colId,
      };
    }

    try {
      await axios
        .put(`/api/dataservice/v1/accounts/${type}-engagement-model`, postData)
        .then((res) => {
          //   console.log(res.data);
          const result = res.data;
          if (type === "account") {
            if (router.pathname === "/spng/task") {
              node?.updateData({
                ...node.data,
                activityInfo: {
                  ...node.data.activityInfo,
                  coverageStatus: result?.[data?.accountKey],
                  aemExpReason: reason?.trim(),
                },
              });
              updateCallback?.(result?.[data?.accountKey]);
              if (valueCheck) {
                api?.forEachNode((n) => {
                  if (
                    n.data.accountKey === data.accountKey &&
                    n.data.activityInfo?.coverageStatus !== result?.[data?.accountKey]
                  ) {
                    n.updateData({
                      ...node.data,
                      activityInfo: {
                        ...node.data.activityInfo,
                        coverageStatus: result?.[data?.accountKey],
                        aemExpReason: reason?.trim(),
                      },
                    });
                  }
                });
                api?.redrawRows();
              }
            } else {
              node?.setDataValue("coverageStatus", result?.[data?.accountKey]);
              node?.setDataValue("aemExpReason", reason?.trim());
              updateCallback?.(result?.[data?.accountKey]);
              if (valueCheck) {
                api?.forEachNode((n) => {
                  if (
                    n.data.accountKey === data.accountKey &&
                    n.data.coverageStatus !== result?.[data?.accountKey]
                  ) {
                    n.setDataValue("coverageStatus", result?.[data?.accountKey]);
                    // if (
                    //   router.pathname?.includes("portfolio") ||
                    //   router.pathname?.includes("summary")
                    // ) {
                    n.setDataValue("aemExpReason", reason?.trim());
                    // }
                  }
                });
                api?.redrawRows();
              }
            }
          } else {
            // update WEM
            updateCallback?.(result?.[data?.accountKey]?.[0], result?.[data?.accountKey]?.[1]);
            // refresh wem by/date
            if (
              router.pathname === "/spng/portfolio" ||
              router.pathname === "/spng/dashboard/executive"
            ) {
              getLatestWEM();
            }
            node?.setDataValue(colId!, result?.[data?.accountKey]?.[0]);
            node?.setDataValue(`${colId}ExpReason`, reason?.trim());

            // update AEM
            node?.setDataValue("coverageStatus", result?.[data?.accountKey]?.[1]);
          }
          setUpdating(false);
          if (router.pathname.includes("summary")) {
            mutate?.();
          }
          onClose();
          toast.success("Update Engagement Model successfully!", {
            position: "top-center",
            autoClose: 3000,
          });
        })
        .catch(() => {
          setUpdating(false);
          toast.error("Fail to update engagement model", {
            position: "top-center",
            autoClose: 3000,
          });
        });
    } catch (e) {
      setUpdating(false);
      toast.error("Fail to update engagement model", {
        position: "top-center",
        autoClose: 3000,
      });
    }
  };

  const onError = (errors: { [key: string]: any }) => {
    if (Object.keys(errors)?.length) {
      toast.error(errors.reason.message, {
        position: "top-center",
        autoClose: 3000,
      });
    }
  };

  useEffect(() => {
    if (open) {
      reset();
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      scroll={"paper"}
      fullWidth={true}
      disableEscapeKeyDown
      // hideBackdrop={true}
      disableEnforceFocus={true}
      disableRestoreFocus={true}
      disableAutoFocus={true}
      maxWidth={"xs"}
      // style={{
      //   overflowY: "auto",
      //   top: 0,
      //   right: 0,
      //   bottom: 0,
      //   left: 0,
      //   margin: "auto",
      //   maxHeight: "84vh",
      // }}
      onClose={onClose}
    >
      <div className={classes.titleContainer}>
        <div>
          <p className={classes.title}>Exception</p>
        </div>
        <IconButton
          size="small"
          onClick={() => onClose()}
        >
          <Close fontSize="small" />
        </IconButton>
      </div>
      <DialogContent
        dividers={true}
        className={classes.contentWrapper}
      >
        <img src="/portfolio/yellow-warning.svg" />
        <div className={classes.mainWrapper}>
          <p className={classes.text}>
            If you want to manually update the engagement model, please fill in the reason.{" "}
          </p>

          <form>
            <div className={classes.container}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                }}
              >
                <Controller
                  control={control}
                  render={(props) => <Reason {...props} />}
                  name={"reason"}
                  defaultValue={null}
                  rules={{
                    required: {
                      message: "This field is required!",
                      value: true,
                    },
                    maxLength: { value: 50, message: "Maximum 50 characters" },
                  }}
                />
              </div>

              <div
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "end",
                  marginTop: 16,
                }}
              >
                <MButton
                  type="reset"
                  buttonType="blue-reverse"
                  // buttonSize={20}
                  size="medium"
                  onClick={onClose}
                >
                  Cancel
                </MButton>
                <MButton
                  type="submit"
                  buttonType="blue"
                  // buttonSize={20}
                  size="medium"
                  disabled={updating}
                  onClick={handleSubmit((data) => onSubmit(data), onError)}
                >
                  Submit
                </MButton>
              </div>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};
export default ExceptionDialog;
